<template>
  <div class="user-search-select">

    <b-form-group :label="label">
      <b-input-group>

        <b-form-input
          v-model="searchTerm"
          :disabled="disabled || loading"
          :placeholder="placeholder"
        />

        <b-input-group-append v-if="searchTerm">
          <b-button
            variant="outline-secondary"
            :disabled="disabled || loading"
            @click="clearSearch"
          >
            Effacer
          </b-button>
        </b-input-group-append>

      </b-input-group>
    </b-form-group>

    <div class="user-search-results mb-3">
      <div v-if="showEmptyHint" class="text-muted small py-2">
        {{ emptyHint }}
      </div>

      <template v-else>

        <div v-if="loading" class="text-center py-3">
          <b-spinner small />
          <div class="text-muted mt-2">Chargement des utilisateurs...</div>
        </div>
        <b-alert v-else-if="error" show variant="danger" class="border">
          {{ error }}
        </b-alert>

        <b-list-group v-else-if="showResults">
          <b-list-group-item
            v-for="user in filteredUsers"
            :key="user.id"
            button
            :active="selectedUserId === user.id"
            class="d-flex justify-content-between align-items-center"
            @click="selectUser(user)"
          >
            <div>
              <div class="font-weight-bold">{{ user.name }}</div>
              <small class="text-muted">{{ user.email}}</small>
            </div>
    
          </b-list-group-item>
        </b-list-group>

        <b-alert v-else show variant="light" class="border text-muted">
          {{ noResultsText }}
        </b-alert>

      </template>

    </div>
  </div>
</template>

<script>
import UserService from "@/services/user.service";
import debounce from "@/utils/debounce";

export default {
  name: "UserSearchSelect",
  props: {
    value: {
      type: Object,
      default: null
    },
    emptyHint: {
      type: String,
      default: "Commencez a taper pour rechercher un utilisateur."
    },
    limit: {
      type: Number,
      default: 8
    },
    label: {
      type: String,
      default: "Recherche"
    },
    placeholder: {
      type: String,
      default: "Nom, prenom ou email"
    },
    noResultsText: {
      type: String,
      default: "Aucun utilisateur ne correspond a la recherche actuelle."
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchTerm: "",
      users: [],
      loading: false,
      error: null,
      usersAbort: null,
      userSearchDebounced: null
    };
  },
  computed: {
    hasSearchTerm() {
      return this.searchTerm.trim().length > 0;
    },
    filteredUsers() {
      return this.users.slice(0, this.limit);
    },
    selectedUserId() {
      return this.value && this.value.id ? this.value.id : null;
    },
    showEmptyHint() {
      return !this.hasSearchTerm;
    },
    showResults() {
      return this.hasSearchTerm && !this.loading && this.filteredUsers.length;
    }
  },
  watch: {
    searchTerm() {
      this.handleSearchTermChange();
    }
  },
  created() {
    this.userSearchDebounced = debounce(term => {
      this.fetchUsers(term);
    }, 350);
  },
  beforeDestroy() {
    this.abortActiveRequest();
    if (this.userSearchDebounced) {
      this.userSearchDebounced.cancel();
    }
  },
  methods: {
    formatErrorMessage(error, fallbackMessage) {
      if (error && error.response && error.response.data) {
        return (
          error.response.data.detail ||
          error.response.data.message ||
          error.response.data.error ||
          fallbackMessage
        );
      }
      return (error && error.message) || fallbackMessage;
    },
    abortActiveRequest() {
      if (this.usersAbort) {
        this.usersAbort.abort();
        this.usersAbort = null;
      }
    },
    async fetchUsers(term = "") {
      this.abortActiveRequest();
      if (!this.hasSearchTerm) {
        this.clearResults();
        return;
      }
      this.usersAbort = new AbortController();
      this.loading = true;
      this.error = null;
      try {
        this.users = await UserService.searchUsers({
          search: term,
          limit: this.limit,
          signal: this.usersAbort.signal
        });
      } catch (err) {
        if (err.name === "AbortError") return;
        this.error = this.formatErrorMessage(
          err,
          "Erreur inattendue lors du chargement des utilisateurs."
        );
      } finally {
        this.loading = false;
      }
    },
    handleSearchTermChange() {
      if (!this.userSearchDebounced) return;
      this.userSearchDebounced(this.searchTerm);
    },
    selectUser(user) {
      if (!user || !user.id) {
        this.$bvToast.toast("Cet utilisateur ne possede pas d'identifiant valide.", {
          title: "Utilisateur invalide",
          variant: "warning",
          solid: true
        });
        return;
      }
      this.$emit("input", user);
      this.$emit("selected", user);
      this.clearSearch();
    },

    clearResults() {
      this.users = [];
      this.error = null;
      this.loading = false;
    },
    clearSelection() {
      this.$emit("input", null);
      this.$emit("cleared");
    },
    clearSearch() {
      this.searchTerm = "";
      this.clearResults();
      this.clearSelection();
    },
    reset() {
      this.abortActiveRequest();
      this.searchTerm = "";
      this.clearResults();
      this.clearSelection();
    }
  }
};
</script>

<style scoped>
.user-search-results .list-group-item {
  cursor: pointer;
}
</style>
